/**
 * Meta Cookie Utilities
 * 
 * This module provides utilities for capturing Meta (Facebook) browser and click IDs
 * that are essential for matching server-side events with user ad clicks.
 * 
 * _fbp (Facebook Browser ID): Generated by the Meta Pixel when a user visits your site
 * _fbc (Facebook Click ID): Generated when a user clicks on a Facebook ad and lands on your site
 * 
 * These values should be captured when a user starts a trial and passed to Chargebee
 * as custom fields, so they're available when the trial converts to paid (server-side).
 */

import localStorageKeys from '@/constants/localStorageKeys';
import { setStringInBrowser, getStringFromBrowser } from '@/utils/browserStorage';

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null
 */
export const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  
  return null;
};

/**
 * Returns the browser user agent.
 * Uses the global `navigator` object (browser-only), guarded for SSR.
 * Important for Meta matching - can improve match quality by up to 21%
 * 
 * @returns {string|null} - User agent string (max 99 chars) or null
 */
export const getUserAgent = () => {
  if (typeof navigator === 'undefined') return null;
  const userAgent = navigator.userAgent || null;
  if (!userAgent) return null;
  
  // Truncate to 99 characters (Chargebee custom field limit)
  return userAgent.substring(0, 99);
};

/**
 * Get the Facebook Browser ID (_fbp cookie)
 * This is set by the Meta Pixel when a user visits your site
 * Format: fb.{subdomain_index}.{creation_time}.{random_number}
 * Example: fb.1.1596403881668.1116446470
 * 
 * @returns {string|null} - The _fbp cookie value or null
 */
export const getFbp = () => {
  return getCookie('_fbp');
};

/**
 * Get the Facebook Click ID (_fbc cookie)
 * This is set when a user clicks on a Facebook ad and lands on your site
 * Format: fb.{subdomain_index}.{creation_time}.{fbclid}
 * Example: fb.1.1596403881668.IwAR1kJl0Jx7...
 * 
 * Note: The fbclid URL parameter is also captured in _fbc
 * 
 * @returns {string|null} - The _fbc cookie value or null
 */
export const getFbc = () => {
  // First check for the cookie
  const fbcCookie = getCookie('_fbc');
  if (fbcCookie) return fbcCookie;
  
  // If no cookie, check URL for fbclid parameter and construct _fbc format
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');
    
    if (fbclid) {
      // Construct _fbc format: fb.{subdomain_index}.{timestamp}.{fbclid}
      const timestamp = Date.now();
      return `fb.1.${timestamp}.${fbclid}`;
    }
  }
  
  return null;
};

/**
 * Get both Meta cookies in a single call
 * @returns {Object} - Object containing fbp and fbc values
 */
export const getMetaCookies = () => {
  return {
    fbp: getFbp(),
    fbc: getFbc(),
  };
};

/**
 * Get the existing FirstPromoter tracking ID
 * This preserves your existing affiliate tracking
 * 
 * @returns {string} - FirstPromoter TID or generated fallback
 */
export const getFPTid = () => {
  if (typeof window !== 'undefined' && window.FPROM && window.FPROM.data && window.FPROM.data.tid) {
    return window.FPROM.data.tid;
  }
  return 'fp_' + Date.now();
};

/**
 * Get all tracking identifiers for Chargebee customer details
 * This combines Meta cookies with FirstPromoter tracking and user agent
 * 
 * @returns {Object} - Object with all custom field values for Chargebee
 */
export const getAllTrackingIds = () => {
  const meta = getMetaCookiesWithFallback();
  const fpTid = getFPTid();
  const userAgent = getUserAgent();

  const trackingIds = {};

  if (fpTid) trackingIds.cf_tid = fpTid;
  if (meta.fbp) trackingIds.cf_fbp = meta.fbp;
  if (meta.fbc) trackingIds.cf_fbc = meta.fbc;
  if (userAgent) trackingIds.cf_user_agent = userAgent;

  // Include start trial event ID for CAPI deduplication
  const startTrialEventId = localStorage.getItem('start_trial_event_id');
  if (startTrialEventId) trackingIds.cf_start_trial_event_id = startTrialEventId;

  return trackingIds;
};

/**
 * Build Chargebee customer details object with all tracking
 * This is a drop-in replacement for your existing customer details setup
 * 
 * @param {Object} user - User object with email, firstname, lastname
 * @param {boolean} isStudioOwner - Whether this is a studio owner account
 * @returns {Object} - Complete customer details object for Chargebee
 */
export const buildChargebeeCustomerDetails = (user, isStudioOwner = false) => {
  if (!user) return {};
  
  const email = user.email?.toLowerCase() || user._id?.toString();
  
  const customerDetails = {
    id: isStudioOwner ? `${email}-studio` : email,
    email: email,
    first_name: user.firstname || user.first_name,
    last_name: user.lastname || user.last_name,
  };
  
  // Add all tracking IDs
  const trackingIds = getAllTrackingIds();
  Object.assign(customerDetails, trackingIds);
  
  return customerDetails;
};

/**
 * Store Meta cookies and user agent in localStorage as backup
 * This can be useful if cookies are cleared before conversion
 * 
 * @returns {void}
 */
export const persistMetaCookiesToStorage = () => {
  if (typeof localStorage === 'undefined') return;
  
  const meta = getMetaCookies();
  const userAgent = getUserAgent();
  
  if (meta.fbp) {
    setStringInBrowser(localStorageKeys.META_FBP, meta.fbp);
  }
  
  if (meta.fbc) {
    setStringInBrowser(localStorageKeys.META_FBC, meta.fbc);
  }
  
  if (userAgent) {
    setStringInBrowser(localStorageKeys.META_USER_AGENT, userAgent);
  }
};

/**
 * Get Meta cookies from localStorage backup
 * Falls back to these if cookies are not available
 * 
 * @returns {Object} - Object containing fbp and fbc from storage
 */
export const getMetaCookiesFromStorage = () => {
  if (typeof localStorage === 'undefined') {
    return { fbp: null, fbc: null };
  }
  
  return {
    fbp: getStringFromBrowser(localStorageKeys.META_FBP),
    fbc: getStringFromBrowser(localStorageKeys.META_FBC),
  };
};

/**
 * Get Meta cookies with storage fallback
 * Tries cookies first, then falls back to localStorage
 * 
 * @returns {Object} - Object containing fbp and fbc values
 */
export const getMetaCookiesWithFallback = () => {
  const cookies = getMetaCookies();
  const storage = getMetaCookiesFromStorage();
  
  return {
    fbp: cookies.fbp || storage.fbp,
    fbc: cookies.fbc || storage.fbc,
  };
};

export default {
  getCookie,
  getUserAgent,
  getFbp,
  getFbc,
  getMetaCookies,
  getFPTid,
  getAllTrackingIds,
  buildChargebeeCustomerDetails,
  persistMetaCookiesToStorage,
  getMetaCookiesFromStorage,
  getMetaCookiesWithFallback,
};
